import { useEffect, useRef, useState } from "react";
import { FaCircleCheck, FaCircleExclamation, FaQrcode, FaRegCircleCheck, FaRotate, FaXmark } from "react-icons/fa6";
import { modalAnim } from "../../components/modal_anim";
import { modalBoxStyle } from "../../components/modal_box_style";
import { ModalOverlay } from "./modal_overlay";
import { WaDeviceService } from "../../../../../../services/WaDeviceService";

export const ScanModal = ({ dataDevice, openModal, setOpenModal, refreshData }) => {
    // 1. State & Ref
    const [data, setData] = useState({
        deviceName: "Device",
        qrImage: null,
        qrLoading: false,
        qrMessage: "",
        isScanned: false,
    });

    const scanPollRef = useRef(null);
    const isScanModalOpenRef = useRef(false);

    // 2. Cleanup & Close Modal
    const handleCloseModal = () => {
        isScanModalOpenRef.current = false;
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }
        setOpenModal(false);
    };

    // 3. Effect Lifecycle
    // Ubah bagian useEffect di ScanModal.jsx
useEffect(() => {
    if (openModal && dataDevice) {
        isScanModalOpenRef.current = true;
        
        // Membaca property sesuai struktur JSON aktual
        const displayName = dataDevice.name 
            ? `${dataDevice.name} (${dataDevice.phone_number || 'Tanpa Nomor'})` 
            : `Device ${dataDevice.phone_number || ''}`;

        setData({
            deviceName: displayName,
            qrImage: null,
            qrLoading: true,
            qrMessage: "Menghubungkan ke gateway...",
            isScanned: false,
        });

        startQRPolling(dataDevice.id);
    } else {
        isScanModalOpenRef.current = false;
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }
    }

    return () => {
        if (scanPollRef.current) clearTimeout(scanPollRef.current);
    };
}, [openModal, dataDevice]);

    // 4. Polling Status & QR Code
    const startQRPolling = (deviceId) => {
        let attempts = 0;
        const maxAttempts = 30;
    
        const poll = async () => {
            if (!isScanModalOpenRef.current) return;
    
            if (attempts >= maxAttempts) {
                setData((prev) => ({
                    ...prev,
                    qrLoading: false,
                    qrMessage: "Waktu sesi habis. Silakan klik Refresh.",
                }));
                return;
            }
    
            attempts++;
    
            try {
                const res = await WaDeviceService.generateQR({
                    params: { id: deviceId }
                });
    
                if (!isScanModalOpenRef.current) return;
    
                const responseData = res?.data || res;
                const isConnected = responseData?.data?.is_connected ?? false;
                const qrCode = responseData?.data?.qr_code ?? null;
    
                // CASE 1: LANGSUNG TERHUBUNG (Sesi aktif)
                if (isConnected) {
                    handleScanSuccess();
                    return;
                }
    
                // CASE 2: QR CODE SIAP DITAMPILKAN
                if (qrCode) {
                    setData((prev) => ({
                        ...prev,
                        qrImage: qrCode,
                        qrLoading: false,
                        qrMessage: "Silakan scan QR Code dengan WhatsApp Anda",
                    }));
    
                    // Jika QR sudah ada, lambatkan polling check (5 detik sekali cukup untuk cek apakah user sudah scan)
                    scanPollRef.current = setTimeout(poll, 5000);
                    return;
                }
    
                // CASE 3: MASIH MENYIAPKAN (Belum ada QR)
                setData((prev) => ({
                    ...prev,
                    qrLoading: true,
                    qrMessage: `Menyiapkan QR Code... (${attempts})`,
                }));
    
                // Cek lagi dalam 3 detik
                scanPollRef.current = setTimeout(poll, 3000);
    
            } catch (error) {
                console.error("[QR Poll Error]:", error);
                if (!isScanModalOpenRef.current) return;
    
                if (attempts < maxAttempts) {
                    scanPollRef.current = setTimeout(poll, 3000);
                } else {
                    setData((prev) => ({
                        ...prev,
                        qrLoading: false,
                        qrMessage: "Gagal terhubung ke gateway WhatsApp.",
                    }));
                }
            }
        };
    
        poll();
    };

    // 5. Handling Scan Sukses
    const handleScanSuccess = () => {
        if (scanPollRef.current) {
            clearTimeout(scanPollRef.current);
            scanPollRef.current = null;
        }

        setData((prev) => ({
            ...prev,
            qrLoading: false,
            qrImage: null,
            qrMessage: "Perangkat berhasil terhubung!",
            isScanned: true,
        }));

        setTimeout(() => {
            handleCloseModal();
            if (typeof refreshData === "function") {
                refreshData();
            }
        }, 1500);
    };

    // 6. Manual Refresh Handler
    const handleRefresh = () => {
        if (scanPollRef.current) clearTimeout(scanPollRef.current);
        setData((prev) => ({
            ...prev,
            qrImage: null,
            qrLoading: true,
            qrMessage: "Memperbarui QR Code...",
        }));
        startQRPolling(dataDevice?.id);
    };

    if (!openModal) return null;

    return (
        <ModalOverlay onClose={handleCloseModal}>
            <style>{modalAnim}</style>
            <div
                className="relative w-full max-w-sm rounded-2xl p-6 shadow-2xl border"
                style={{
                    ...modalBoxStyle,
                    animation: "modalIn 0.25s ease-out",
                }}
            >
                <button
                    onClick={handleCloseModal}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
                >
                    <FaXmark size={16} />
                </button>

                <div className="text-center">
                    {/* Header Icon */}
                    <div
                        className={`w-12 h-12 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-colors duration-300 ${
                            data.isScanned
                                ? "bg-emerald-500/10 border-emerald-500/20"
                                : "bg-cyan-500/10 border-cyan-500/20"
                        }`}
                    >
                        {data.isScanned ? (
                            <FaRegCircleCheck className="text-emerald-400 text-lg" />
                        ) : (
                            <FaQrcode className="text-cyan-400 text-lg" />
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-gray-100 mb-1">
                        {data.deviceName}
                    </h3>

                    {/* Pesan Status */}
                    <p
                        className={`text-xs mb-6 px-2 leading-relaxed transition-colors duration-300 ${
                            data.isScanned
                                ? "text-emerald-400 font-semibold"
                                : "text-slate-400"
                        }`}
                    >
                        {data.qrMessage}
                    </p>

                    {/* QR Code / Loading / Success Container */}
                    {data.isScanned ? (
                        <div className="w-56 h-56 mx-auto mb-6 flex items-center justify-center">
                            <FaCircleCheck
                                className="text-emerald-400"
                                style={{
                                    fontSize: "80px",
                                    animation: "successPop 0.5s ease-out",
                                }}
                            />
                        </div>
                    ) : (
                        <div className="w-56 h-56 mx-auto bg-white rounded-2xl p-3 flex items-center justify-center mb-6 relative overflow-hidden shadow-lg shadow-black/20">
                            {data.qrLoading && (
                                <div className="absolute inset-0 bg-white flex flex-col items-center justify-center z-10 rounded-2xl">
                                    <div className="w-8 h-8 border-[3px] border-cyan-500 border-t-transparent rounded-full animate-spin mb-3" />
                                    <span className="text-slate-500 text-xs font-medium">
                                        Memproses...
                                    </span>
                                </div>
                            )}
                            {data.qrImage && !data.qrLoading && (
                                <img
                                    src={data.qrImage}
                                    alt="QR Code"
                                    className="w-full h-full object-contain rounded-lg"
                                />
                            )}
                            {!data.qrLoading && !data.qrImage && (
                                <div className="flex flex-col items-center justify-center text-center px-4">
                                    <FaCircleExclamation className="text-slate-300 text-2xl mb-2" />
                                    <span className="text-slate-500 text-xs">
                                        {data.qrMessage || "Terjadi kesalahan"}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    {!data.isScanned && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                disabled={data.qrLoading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <FaRotate size={11} /> Refresh
                            </button>
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all bg-red-500/8 text-red-400 hover:bg-red-500/15 border border-red-500/15"
                            >
                                Tutup
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </ModalOverlay>
    );
};